// Общото между списъка с правила за взаимозаменяемост и страницата за
// създаване/редакция: редът в редактора, форматирането на количествата в
// равенството и „веригата" за показване („1 кг Кафе A = 0.7 кг Кафе B").
//
// ВАЖНО: записаният коефициент („колко базови единици на групата е 1 единица
// от продукта") НЕ се показва никъде — потребителят вижда и въвежда
// КОЛИЧЕСТВА в равенство. Преводът в двете посоки е в `interchangeRuleMath.ts`.
import { amountsFromCoefficients } from "./interchangeRuleMath";

// Един продукт в редактора на правило
export type InterchangeMemberDraft = {
  key: number; // локален ключ на картата (стабилен при преподреждане)
  productId: number;
  productName: string;
  unit: string | null;
  // количеството в равенството („= 0.7 кг"); null — празно поле
  amount: number | null;
  // записаният коефициент; null за нов продукт и щом количеството се смени
  savedCoefficient: number | null;
  // записаният приоритет; null за нов продукт
  savedSortIndex: number | null;
};

// id на полето за количество на картата — по него веригата и страницата
// местят фокуса (↑/↓/Enter между количествата, ↓ „отникъде")
export const amountInputId = (key: number) => `ich-amount-${key}`;

// Количествата в равенството са СЪОТНОШЕНИЯ → показват се с ТОЧКА (0.7), до 3
// знака, без излишни нули — като полето, в което се въвеждат. Изчислените
// количества в примера „Ако липсват…" са с запетая (`formatPreviewQty`).
export const formatAmount = (value: number) =>
  Number(value ?? 0)
    .toLocaleString("bg-BG", { maximumFractionDigits: 3 })
    .replace(",", ".");

// Полето за количество: приема и точка, И запетая (до 3 знака), но се показва
// ВИНАГИ с ТОЧКА — въведената запетая става точка още при писане.
export const amountParser = (value?: string): string => {
  if (!value) return "";
  const cleaned = value.replace(/,/g, ".").replace(/[^0-9.]/g, "");
  const parts = cleaned.split(".");
  if (parts.length === 1) return parts[0];
  return `${parts[0]}.${parts.slice(1).join("").slice(0, 3)}`;
};

export const amountFormatter = (
  value?: string | number,
  info?: { userTyping: boolean; input: string },
): string => {
  if (value === undefined || value === null || value === "") return "";
  // Докато се пише — стойността от parser-а (запетаята вече е точка).
  if (info?.userTyping) return String(value);
  // В покой — без излишни нули (1 / 0.7 / 0.125), с точка.
  return String(Number(value));
};

// Количество в примера — със ЗАПЕТАЯ (количествата в склада са така) и до
// толкова знака, колкото позволява мерната единица, без излишни нули в текста.
export const formatPreviewQty = (value: number, decimals: number) =>
  Number(value ?? 0).toLocaleString("bg-BG", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });

// Едно звено на веригата в списъка: „0.7 кг Кафе B"
export type InterchangeChainLink = {
  productId: number;
  productName: string;
  unit: string | null;
  amount: number | null;
};

export const chainLinksOf = (
  members: { productId: number; productName: string; unit: string | null; coefficient: number }[],
): InterchangeChainLink[] => {
  const amounts = amountsFromCoefficients(members.map((member) => member.coefficient));
  return members.map((member, index) => ({
    productId: member.productId,
    productName: member.productName,
    unit: member.unit,
    amount: amounts[index],
  }));
};
